import { MCAT_SUBJECTS as MCAT_SECTIONS } from 'my-core/mcat-data';

import { SUBJECT_CONFIG } from './course-utils';

export const MCAT_SUBJECTS = [
  { key: 'chemistry', name: 'Chemistry', abbrv: 'GCH', sections: ['cpf', 'bbf'], icon: SUBJECT_CONFIG.chemistry.icon },
  { key: 'biochem', name: 'Biochemistry', abbrv: 'BCH', sections: ['cpf', 'bbf'], icon: SUBJECT_CONFIG.biochem.icon },
  { key: 'biology', name: 'Biology', abbrv: 'BIO', sections: ['cpf', 'bbf', 'psf'], icon: SUBJECT_CONFIG.biology.icon },
  {
    key: 'orgchem',
    name: 'Organic Chemistry',
    abbrv: 'OCH',
    sections: ['cpf', 'bbf'],
    icon: SUBJECT_CONFIG.orgchem.icon,
  },
  { key: 'physics', name: 'Physics', abbrv: 'PHY', sections: ['cpf', 'bbf'], icon: SUBJECT_CONFIG.physics.icon },
  { key: 'psychology', name: 'Psychology', abbrv: 'PSY', sections: ['psf'], icon: SUBJECT_CONFIG.psychology.icon },
  { key: 'sociology', name: 'Sociology', abbrv: 'SOC', sections: ['psf'], icon: SUBJECT_CONFIG.reading.icon },
  { key: 'socsci', name: 'Social Science', abbrv: 'SSC', sections: ['cars'], icon: SUBJECT_CONFIG.reading.icon },
  { key: 'humanities', name: 'Humanities', abbrv: 'HUM', sections: ['cars'], icon: SUBJECT_CONFIG.english.icon },
];
export const MCAT_SUBJECTS_MAP = Object.fromEntries(MCAT_SUBJECTS.map(s => [s.key, s]));

export const CARS_TOPICS = [
  { key: 'GOV', name: 'Government' },
  { key: 'LIT', name: 'Literature' },
  { key: 'HIS', name: 'History' },
  { key: 'SOC', name: 'Sociology' },
  { key: 'EDU', name: 'Education' },
  { key: 'PHI', name: 'Philosophy' },
  { key: 'THE', name: 'Theater' },
  { key: 'AHS', name: 'Art History' },
  { key: 'RGN', name: 'Religion' },
  { key: 'PSY', name: 'Psychology' },
  { key: 'POP', name: 'Population Health' },
  { key: 'ART', name: 'Art' },
  { key: 'MUS', name: 'Music' },
  { key: 'LAN', name: 'Language' },
  { key: 'ECO', name: 'Economy' },
  { key: 'ANT', name: 'Anthropology' },
  { key: 'BUS', name: 'Business' },
  { key: 'ETH', name: 'Ethics' },
  { key: 'ARC', name: 'Archeology' },
  { key: 'FIL', name: 'Film' },
];
export const CARS_TOPICS_MAP = Object.fromEntries(CARS_TOPICS.map(t => [t.key, t]));

export const MCAT_SKILLS = [
  {
    key: 'SIRS1',
    type: 'SIR',
    sections: ['cpf', 'bbf', 'psf'],
    name: 'Knowledge of Scientific Concepts and Principles',
    description: 'Understanding and applying scientific facts, definitions, and relationships.',
    color: 'purple',
  },
  {
    key: 'SIRS2',
    type: 'SIR',
    sections: ['cpf', 'bbf', 'psf'],
    name: 'Scientific Reasoning and Problem-Solving',
    description: 'Using scientific principles to explain and predict behaviors of systems.',
    color: 'skyblue',
  },
  {
    key: 'SIRS3',
    type: 'SIR',
    sections: ['cpf', 'bbf', 'psf'],
    name: 'Reasoning About Research Design',
    description: 'Interpreting experiments, controls, bias, variables, and methodology.',
    color: 'green',
  },
  {
    key: 'SIRS4',
    type: 'SIR',
    sections: ['cpf', 'bbf', 'psf'],
    name: 'Data-Based and Statistical Reasoning',
    description: 'Interpreting graphs, charts, data trends, and statistical information.',
    color: 'yellow',
  },
  {
    key: 'CMP',
    type: 'Foundations of Comprehension',
    sections: ['cars'],
    name: 'Foundations of Comprehension - Basic Understanding',
    description: 'Understanding main ideas, purpose, claims, definitions, and paragraph function.',
    color: 'secondary',
  },
  {
    key: 'RWT',
    type: 'Reasoning Within The Text',
    sections: ['cars'],
    name: 'Reasoning Within the Text - Integration',
    description: 'Integrating ideas across paragraphs and synthesizing information.',
    color: 'cyan',
  },
  {
    key: 'RBT',
    type: 'Reasoning Beyond the Text',
    sections: ['cars'],
    name: 'Reasoning Beyond the Text - Application',
    description: 'Applying passage ideas to new contexts or scenarios.',
    color: 'primary',
  },
];

let topicsTree = null;
export function getMCATTopicsTree() {
  if (topicsTree) return topicsTree;
  const topicsNodes = Object.fromEntries(TOPICS.map(topic => [topic.key, { ...topic, children: [] }]));
  TOPICS.forEach(topic => {
    if (topic.parent) {
      topicsNodes[topic.parent].children.push(topicsNodes[topic.key]);
    }
  });
  topicsTree = Object.values(topicsNodes).filter(node => !node.parent);
  topicsTree._nodeMap = topicsNodes;
  return topicsTree;
}

const TOPICS = [
  {
    key: 'FC1',
    name: 'Biomolecules Have Unique Properties That Determine Their Function',
    description:
      'Biological molecules—proteins, nucleic acids, carbohydrates, and lipids—have specific chemical and physical properties that determine their structure, interactions, and function in living systems.',
  },
  {
    key: 'FC2',
    name: 'Highly Organized Assemblies of Molecules, Cells, and Organs Carry Out the Functions of Living Organisms',
    description:
      'Cells, tissues, organs, and organ systems are organized in a hierarchical manner, enabling complex functions necessary to maintain life.',
  },
  {
    key: 'FC3',
    name: 'Complex Systems of Tissues and Organs Sense the Internal and External Environments and Respond Appropriately',
    description:
      'The nervous and endocrine systems detect stimuli and coordinate responses that maintain homeostasis and enable adaptation to environmental changes.',
  },
  {
    key: 'FC4',
    name: 'Physical Principles Underlie Biological Processes',
    description:
      'Core principles of physics, including mechanics, fluid dynamics, electricity, light, and sound, explain the structure and function of biological systems.',
  },
  {
    key: 'FC5',
    name: 'Chemical Principles Underlie Biological Processes',
    description:
      'Chemical interactions, thermodynamics, kinetics, and molecular structure govern biological reactions and processes.',
  },
  {
    key: 'FC6',
    name: 'Biological, Psychological, and Sociocultural Factors Influence the Ways That Individuals Perceive, Think, and Act',
    description:
      'Perception, cognition, emotion, and behavior are shaped by biological mechanisms, psychological processes, and sociocultural influences.',
  },
  {
    key: 'FC7',
    name: 'Biological, Psychological, and Sociocultural Factors Influence Behavior and Behavior Change',
    description:
      'Learning, attitudes, social norms, and group dynamics influence behavior and the capacity for behavioral change.',
  },
  {
    key: 'FC8',
    name: 'Psychological, Sociocultural, and Biological Factors Influence the Way We Think About Ourselves and Others',
    description: 'Identity, attitudes, beliefs, and interpersonal processes shape self-concept and social interaction.',
  },
  {
    key: 'FC9',
    name: 'Social Structure and Demographic Factors Shape Health and Behavior',
    description:
      'Social institutions, structures, and demographic patterns influence human behavior, health outcomes, and access to resources.',
  },
  {
    key: 'FC10',
    name: 'Social Stratification and Inequality Shape Health and Well-Being',
    description:
      'Social class, stratification, and inequality affect opportunity, social mobility, health disparities, and life outcomes.',
  },
  {
    key: 'CC1A',
    parent: 'FC1',
    name: 'Protein Structure and Function',
    description:
      'Focuses on the structural and functional complexity of proteins, derived from amino acid composition, sequence, covalent bonding, and three-dimensional folding in aqueous environments.',
  },
  {
    key: 'CC1A.1',
    parent: 'CC1A',
    name: 'Amino Acids',
    subjects: ['biochem', 'orgchem'],
  },
  {
    key: 'CC1A.2',
    parent: 'CC1A',
    name: 'Protein Structure',
    subjects: ['biology', 'biochem', 'orgchem'],
  },
  {
    key: 'CC1A.3',
    parent: 'CC1A',
    name: 'Nonenzymatic Protein Function',
    subjects: ['biology', 'biochem'],
  },
  {
    key: 'CC1A.4',
    parent: 'CC1A',
    name: 'Enzyme Structure and Function',
    subjects: ['biology', 'biochem'],
  },
  {
    key: 'CC1A.5',
    parent: 'CC1A',
    name: 'Control of Enzyme Activity',
    subjects: ['biology', 'biochem'],
  },
  {
    key: 'CC1B',
    parent: 'FC1',
    name: 'Transmission of Genetic Information',
    description:
      'Focuses on molecular mechanisms that transfer sequence-specific biological information between biopolymers, leading to protein synthesis and supporting normal cellular function and disease processes.',
  },
  {
    key: 'CC1B.1',
    parent: 'CC1B',
    name: 'Nucleic Acid Structure and Function',
    subjects: ['biology', 'biochem'],
  },
  {
    key: 'CC1B.2',
    parent: 'CC1B',
    name: 'DNA Replication',
    subjects: ['biology'],
  },
  {
    key: 'CC1B.3',
    parent: 'CC1B',
    name: 'Repair of DNA',
    subjects: ['biology'],
  },
  {
    key: 'CC1B.4',
    parent: 'CC1B',
    name: 'Genetic Code',
    subjects: ['biology'],
  },
  {
    key: 'CC1B.5',
    parent: 'CC1B',
    name: 'Transcription',
    subjects: ['biology'],
  },
  {
    key: 'CC1B.6',
    parent: 'CC1B',
    name: 'Translation',
    subjects: ['biology'],
  },
  {
    key: 'CC1B.7',
    parent: 'CC1B',
    name: 'Eukaryotic Chromosome Organization',
    subjects: ['biology'],
  },
  {
    key: 'CC1B.8',
    parent: 'CC1B',
    name: 'Control of Gene Expression in Prokaryotes',
    subjects: ['biology'],
  },
  {
    key: 'CC1B.9',
    parent: 'CC1B',
    name: 'Control of Gene Expression in Eukaryotes',
    subjects: ['biology'],
  },
  {
    key: 'CC1B.10',
    parent: 'CC1B',
    name: 'Recombinant DNA and Biotechnology',
    subjects: ['biology'],
  },
  {
    key: 'CC1C',
    parent: 'FC1',
    name: 'DNA Replication and Heredity',
    description:
      'Focuses on mechanisms that transmit heritable information stored in DNA from one generation to the next.',
  },
  {
    key: 'CC1C.1',
    parent: 'CC1C',
    name: 'Evidence That DNA Is Genetic Material',
    subjects: ['biology'],
  },
  {
    key: 'CC1C.2',
    parent: 'CC1C',
    name: 'Mendelian Concepts',
    subjects: ['biology'],
  },
  {
    key: 'CC1C.3',
    parent: 'CC1C',
    name: 'Meiosis and Other Factors Affecting Genetic Variability',
    subjects: ['biology'],
  },
  {
    key: 'CC1C.4',
    parent: 'CC1C',
    name: 'Analytic Methods',
    subjects: ['biology'],
  },
  {
    key: 'CC1C.5',
    parent: 'CC1C',
    name: 'Evolution',
    subjects: ['biology'],
  },
  {
    key: 'CC1D',
    parent: 'FC1',
    name: 'Bioenergetics and Metabolism',
    description:
      'Focuses on biomolecules and regulated metabolic pathways involved in harvesting chemical energy from fuel molecules to drive biological processes.',
  },
  {
    key: 'CC1D.1',
    parent: 'CC1D',
    name: 'Principles of Bioenergetics',
    subjects: ['biochem', 'chemistry'],
  },
  {
    key: 'CC1D.2',
    parent: 'CC1D',
    name: 'Carbohydrates',
    subjects: ['biochem', 'orgchem'],
  },
  {
    key: 'CC1D.3',
    parent: 'CC1D',
    name: 'Glycolysis, Gluconeogenesis, and the Pentose Phosphate Pathway',
    subjects: ['biology', 'biochem'],
  },
  {
    key: 'CC1D.4',
    parent: 'CC1D',
    name: 'Principles of Metabolic Regulation',
    subjects: ['biochem'],
  },
  {
    key: 'CC1D.5',
    parent: 'CC1D',
    name: 'Citric Acid Cycle',
    subjects: ['biology', 'biochem'],
  },
  {
    key: 'CC1D.6',
    parent: 'CC1D',
    name: 'Metabolism of Fatty Acids and Proteins',
    subjects: ['biology', 'biochem'],
  },
  {
    key: 'CC1D.7',
    parent: 'CC1D',
    name: 'Oxidative Phosphorylation',
    subjects: ['biology', 'biochem'],
  },
  {
    key: 'CC1D.8',
    parent: 'CC1D',
    name: 'Hormonal Regulation and Integration of Metabolism',
    subjects: ['biochem'],
  },
  {
    key: 'CC2A',
    parent: 'FC2',
    name: 'Cell Structure and Organization',
    description:
      'Focuses on molecular assemblies, cells, and groups of cells within unicellular and multicellular organisms that carry out life-sustaining processes.',
  },
  {
    key: 'CC2A.1',
    parent: 'CC2A',
    name: 'Plasma Membrane',
    subjects: ['biology', 'biochem'],
  },
  {
    key: 'CC2A.2',
    parent: 'CC2A',
    name: 'Membrane-Bound Organelles and Defining Characteristics of Eukaryotic Cells',
    subjects: ['biology'],
  },
  {
    key: 'CC2A.3',
    parent: 'CC2A',
    name: 'Cytoskeleton',
    subjects: ['biology'],
  },
  {
    key: 'CC2A.4',
    parent: 'CC2A',
    name: 'Tissues Formed From Eukaryotic Cells',
    subjects: ['biology'],
  },
  {
    key: 'CC2B',
    parent: 'FC2',
    name: 'Prokaryotes and Viruses',
    description:
      'Focuses on the structure, growth, physiology, and genetics of prokaryotes, as well as the structure and life cycles of viruses.',
  },
  {
    key: 'CC2B.1',
    parent: 'CC2B',
    name: 'Cell Theory',
    subjects: ['biology'],
  },
  {
    key: 'CC2B.2',
    parent: 'CC2B',
    name: 'Classification and Structure of Prokaryotic Cells',
    subjects: ['biology'],
  },
  {
    key: 'CC2B.3',
    parent: 'CC2B',
    name: 'Growth and Physiology of Prokaryotic Cells',
    subjects: ['biology'],
  },
  {
    key: 'CC2B.4',
    parent: 'CC2B',
    name: 'Genetics of Prokaryotic Cells',
    subjects: ['biology'],
  },
  {
    key: 'CC2B.5',
    parent: 'CC2B',
    name: 'Virus Structure',
    subjects: ['biology'],
  },
  {
    key: 'CC2B.6',
    parent: 'CC2B',
    name: 'Viral Life Cycle',
    subjects: ['biology'],
  },
  {
    key: 'CC2C',
    parent: 'FC2',
    name: 'Cell Division and Differentiation',
    description:
      'Focuses on the processes of cell and nuclear division and the mechanisms governing cell differentiation and specialization.',
  },
  {
    key: 'CC2C.1',
    parent: 'CC2C',
    name: 'Mitosis',
    subjects: ['biology'],
  },
  {
    key: 'CC2C.2',
    parent: 'CC2C',
    name: 'Biosignaling',
    subjects: ['biochem'],
    detail: 'Oncogenes, Apoptosis',
  },
  {
    key: 'CC2C.3',
    parent: 'CC2C',
    name: 'Reproductive System',
    subjects: ['biology'],
    detail: 'Gametogenesis, Reproductive Sequence',
  },
  {
    key: 'CC2C.4',
    parent: 'CC2C',
    name: 'Embryogenesis',
    subjects: ['biology'],
  },
  {
    key: 'CC2C.5',
    parent: 'CC2C',
    name: 'Mechanisms of Development',
    subjects: ['biology'],
  },
  {
    key: 'CC3A',
    parent: 'FC3',
    name: 'Nervous and Endocrine Systems',
    description:
      'Focuses on the structure and function of the nervous and endocrine systems and how they coordinate responses to internal and external stimuli.',
  },
  {
    key: 'CC3A.1',
    parent: 'CC3A',
    name: 'Nervous System: Structure and Function',
    subjects: ['biology'],
  },
  {
    key: 'CC3A.2',
    parent: 'CC3A',
    name: 'Nerve Cell',
    subjects: ['biology'],
  },
  {
    key: 'CC3A.3',
    parent: 'CC3A',
    name: 'Electrochemistry',
    subjects: ['chemistry'],
  },
  {
    key: 'CC3A.4',
    parent: 'CC3A',
    name: 'Biosignaling',
    subjects: ['biochem'],
    detail: 'Gated channels, Receptors',
  },
  {
    key: 'CC3A.5',
    parent: 'CC3A',
    name: 'Lipids',
    subjects: ['biochem', 'orgchem'],
  },
  {
    key: 'CC3A.6',
    parent: 'CC3A',
    name: 'Endocrine System: Hormones and Their Sources',
    subjects: ['biology'],
  },
  {
    key: 'CC3A.7',
    parent: 'CC3A',
    name: 'Endocrine System: Mechanisms of Hormone Action',
    subjects: ['biology'],
  },
  {
    key: 'CC3B',
    parent: 'FC3',
    name: 'Organ Systems',
    description:
      'Focuses on the structure and function of major organ systems and how they interact to maintain and sustain the organism.',
  },
  {
    key: 'CC3B.1',
    parent: 'CC3B',
    name: 'Respiratory System',
    subjects: ['biology'],
  },
  {
    key: 'CC3B.2',
    parent: 'CC3B',
    name: 'Circulatory System',
    subjects: ['biology'],
  },
  {
    key: 'CC3B.3',
    parent: 'CC3B',
    name: 'Lymphatic System',
    subjects: ['biology'],
  },
  {
    key: 'CC3B.4',
    parent: 'CC3B',
    name: 'Immune System',
    subjects: ['biology'],
  },
  {
    key: 'CC3B.5',
    parent: 'CC3B',
    name: 'Digestive System',
    subjects: ['biology'],
  },
  {
    key: 'CC3B.6',
    parent: 'CC3B',
    name: 'Excretory System',
    subjects: ['biology'],
  },
  {
    key: 'CC3B.7',
    parent: 'CC3B',
    name: 'Reproductive System',
    subjects: ['biology'],
    detail: 'Reproductive structures, Hormonal Control',
  },
  {
    key: 'CC3B.8',
    parent: 'CC3B',
    name: 'Muscle System',
    subjects: ['biology'],
  },
  {
    key: 'CC3B.9',
    parent: 'CC3B',
    name: 'Specialized Cell - Muscle Cell',
    subjects: ['biology'],
  },
  {
    key: 'CC3B.10',
    parent: 'CC3B',
    name: 'Skeletal System',
    subjects: ['biology'],
  },
  {
    key: 'CC3B.11',
    parent: 'CC3B',
    name: 'Skin System',
    subjects: ['biology'],
  },
  {
    key: 'CC4A',
    parent: 'FC4',
    name: 'Motion and Energy',
    description:
      'Focuses on motion, its causes, and various forms of energy and energy transformations relevant to biological systems.',
  },
  {
    key: 'CC4A.1',
    parent: 'CC4A',
    name: 'Translational Motion',
    subjects: ['physics'],
  },
  {
    key: 'CC4A.2',
    parent: 'CC4A',
    name: 'Force',
    subjects: ['physics'],
  },
  {
    key: 'CC4A.3',
    parent: 'CC4A',
    name: 'Equilibrium',
    subjects: ['physics'],
  },
  {
    key: 'CC4A.4',
    parent: 'CC4A',
    name: 'Work',
    subjects: ['physics'],
  },
  {
    key: 'CC4A.5',
    parent: 'CC4A',
    name: 'Energy of Point Object Systems',
    subjects: ['physics'],
  },
  {
    key: 'CC4A.6',
    parent: 'CC4A',
    name: 'Periodic Motion',
    subjects: ['physics'],
  },
  {
    key: 'CC4B',
    parent: 'FC4',
    name: 'Fluids',
    description: 'Focuses on the behavior of fluids and their relevance to pulmonary and circulatory system function.',
  },
  {
    key: 'CC4B.1',
    parent: 'CC4B',
    name: 'Fluids',
    subjects: ['physics'],
  },
  {
    key: 'CC4B.2',
    parent: 'CC4B',
    name: 'Circulatory System',
    subjects: ['biology'],
  },
  {
    key: 'CC4B.3',
    parent: 'CC4B',
    name: 'Gas Phase',
    subjects: ['chemistry', 'physics'],
  },
  {
    key: 'CC4C',
    parent: 'FC4',
    name: 'Electricity and Magnetism',
    description:
      'Focuses on electrical currents, voltages, energy conversion to electrical forms, and electrical signal transmission in biological systems, particularly the nervous system.',
  },
  {
    key: 'CC4C.1',
    parent: 'CC4C',
    name: 'Electrostatics',
    subjects: ['physics'],
  },
  {
    key: 'CC4C.2',
    parent: 'CC4C',
    name: 'Circuit Elements',
    subjects: ['physics'],
  },
  {
    key: 'CC4C.3',
    parent: 'CC4C',
    name: 'Magnetism',
    subjects: ['physics'],
  },
  {
    key: 'CC4C.4',
    parent: 'CC4C',
    name: 'Electrochemistry',
    subjects: ['chemistry'],
  },
  {
    key: 'CC4C.5',
    parent: 'CC4C',
    name: 'Specialized Cell - Nerve Cell',
    subjects: ['biology'],
  },
  {
    key: 'CC4D',
    parent: 'FC4',
    name: 'Light and Sound',
    description:
      'Focuses on the properties of light and sound, their interactions with matter, sensory perception, and generation of structural or imaging information.',
  },
  {
    key: 'CC4D.1',
    parent: 'CC4D',
    name: 'Sound',
    subjects: ['physics'],
  },
  {
    key: 'CC4D.2',
    parent: 'CC4D',
    name: 'Light, Electromagnetic Radiation',
    subjects: ['physics'],
  },
  {
    key: 'CC4D.3',
    parent: 'CC4D',
    name: 'Molecular Structure and Absorption Spectra',
    subjects: ['orgchem'],
  },
  {
    key: 'CC4D.4',
    parent: 'CC4D',
    name: 'Geometrical Optics',
    subjects: ['physics'],
  },
  {
    key: 'CC4E',
    parent: 'FC4',
    name: 'Atomic and Nuclear Phenomena',
    description:
      'Focuses on subatomic particles, atomic structure, nuclear radiation, and how atomic configuration predicts physical and chemical properties.',
  },
  {
    key: 'CC4E.1',
    parent: 'CC4E',
    name: 'Atomic Nucleus',
    subjects: ['physics', 'chemistry'],
  },
  {
    key: 'CC4E.2',
    parent: 'CC4E',
    name: 'Electronic Structure',
    subjects: ['physics', 'chemistry'],
  },
  {
    key: 'CC4E.3',
    parent: 'CC4E',
    name: 'The Periodic Table - Classification of Elements Into Groups by Electronic Structure',
    subjects: ['chemistry'],
  },
  {
    key: 'CC4E.4',
    parent: 'CC4E',
    name: 'The Periodic Table - Variations of Chemical Properties with Group and Row',
    subjects: ['chemistry'],
  },
  {
    key: 'CC4E.5',
    parent: 'CC4E',
    name: 'Stoichiometry',
    subjects: ['chemistry'],
  },
  {
    key: 'CC5A',
    parent: 'FC5',
    name: 'Water and Solutions',
    description: 'Focuses on the unique nature of water and aqueous solutions and their role in biological systems.',
  },
  {
    key: 'CC5A.1',
    parent: 'CC5A',
    name: 'Acid-Base Equilibria',
    subjects: ['chemistry', 'biochem'],
  },
  {
    key: 'CC5A.2',
    parent: 'CC5A',
    name: 'Ions in Solutions',
    subjects: ['chemistry', 'biochem'],
  },
  {
    key: 'CC5A.3',
    parent: 'CC5A',
    name: 'Solubility',
    subjects: ['chemistry'],
  },
  {
    key: 'CC5A.4',
    parent: 'CC5A',
    name: 'Titration',
    subjects: ['chemistry'],
  },
  {
    key: 'CC5B',
    parent: 'FC5',
    name: 'Molecular Interactions',
    description: 'Focuses on molecular structure and intermolecular forces governing chemical behavior.',
  },
  {
    key: 'CC5B.1',
    parent: 'CC5B',
    name: 'Covalent Bond',
    subjects: ['chemistry'],
  },
  {
    key: 'CC5B.2',
    parent: 'CC5B',
    name: 'Liquid Phase - Intermolecular Forces',
    subjects: ['chemistry'],
  },
  {
    key: 'CC5C',
    parent: 'FC5',
    name: 'Separation and Purification Techniques',
    description:
      'Focuses on laboratory methods used to separate, purify, and analyze chemical and biological substances.',
  },
  {
    key: 'CC5C.1',
    parent: 'CC5C',
    name: 'Separations and Purifications',
    subjects: ['orgchem', 'biochem'],
  },
  {
    key: 'CC5D',
    parent: 'FC5',
    name: 'Biologically Relevant Molecules',
    description: 'Focuses on the structure, function, and reactivity of biologically important molecules.',
  },
  {
    key: 'CC5D.1',
    parent: 'CC5D',
    name: 'Nucleotides and Nucleic Acids',
    subjects: ['biochem', 'biology'],
  },
  {
    key: 'CC5D.2',
    parent: 'CC5D',
    name: 'Amino Acids, Peptides, Proteins',
    subjects: ['orgchem', 'biochem'],
  },
  {
    key: 'CC5D.3',
    parent: 'CC5D',
    name: 'The Three-Dimensional Protein Structure',
    subjects: ['biochem'],
  },
  {
    key: 'CC5D.4',
    parent: 'CC5D',
    name: 'Nonenzymatic Protein Function',
    subjects: ['biochem'],
  },
  {
    key: 'CC5D.5',
    parent: 'CC5D',
    name: 'Lipids',
    subjects: ['biochem', 'orgchem'],
  },
  {
    key: 'CC5D.6',
    parent: 'CC5D',
    name: 'Carbohydrates',
    subjects: ['orgchem'],
  },
  {
    key: 'CC5D.7',
    parent: 'CC5D',
    name: 'Aldehydes and Ketones',
    subjects: ['orgchem'],
  },
  {
    key: 'CC5D.8',
    parent: 'CC5D',
    name: 'Alcohols',
    subjects: ['orgchem'],
  },
  {
    key: 'CC5D.9',
    parent: 'CC5D',
    name: 'Carboxylic Acids',
    subjects: ['orgchem'],
  },
  {
    key: 'CC5D.10',
    parent: 'CC5D',
    name: 'Acid Derivatives (Anhydrides, Amides, Esters)',
    subjects: ['orgchem'],
  },
  {
    key: 'CC5D.11',
    parent: 'CC5D',
    name: 'Phenols',
    subjects: ['orgchem', 'biochem'],
  },
  {
    key: 'CC5D.12',
    parent: 'CC5D',
    name: 'Polycyclic and Heterocyclic Aromatic Compounds',
    subjects: ['orgchem', 'biochem'],
  },
  {
    key: 'CC5E',
    parent: 'FC5',
    name: 'Thermodynamics and Kinetics',
    description: 'Focuses on principles of chemical thermodynamics and kinetics as they apply to biological systems.',
  },
  {
    key: 'CC5E.1',
    parent: 'CC5E',
    name: 'Enzymes',
    subjects: ['biochem', 'biology'],
  },
  {
    key: 'CC5E.2',
    parent: 'CC5E',
    name: 'Principles of Bioenergetics',
    subjects: ['biochem'],
  },
  {
    key: 'CC5E.3',
    parent: 'CC5E',
    name: 'Energy Changes in Chemical Reactions - Thermochemistry, Thermodynamics',
    subjects: ['chemistry', 'physics'],
  },
  {
    key: 'CC5E.4',
    parent: 'CC5E',
    name: 'Rate Processes in Chemical Reactions - Kinetics and Equilibrium',
    subjects: ['chemistry'],
  },
  {
    key: 'CC6A',
    parent: 'FC6',
    name: 'Sensory Processing',
    description: 'Focuses on mechanisms used to sense environmental stimuli.',
  },
  {
    key: 'CC6A.1',
    parent: 'CC6A',
    name: 'Sensory Processing',
    subjects: ['psychology', 'biology'],
  },
  {
    key: 'CC6A.2',
    parent: 'CC6A',
    name: 'Vision',
    subjects: ['psychology', 'biology'],
  },
  {
    key: 'CC6A.3',
    parent: 'CC6A',
    name: 'Hearing',
    subjects: ['psychology', 'biology'],
  },
  {
    key: 'CC6A.4',
    parent: 'CC6A',
    name: 'Other Senses',
    subjects: ['psychology', 'biology'],
  },
  {
    key: 'CC6A.5',
    parent: 'CC6A',
    name: 'Perception',
    subjects: ['psychology'],
  },
  {
    key: 'CC6B',
    parent: 'FC6',
    name: 'Cognitive Processing',
    description: 'Focuses on how organisms interpret and make sense of environmental information.',
  },
  {
    key: 'CC6B.1',
    parent: 'CC6B',
    name: 'Attention',
    subjects: ['psychology'],
  },
  {
    key: 'CC6B.2',
    parent: 'CC6B',
    name: 'Cognition',
    subjects: ['psychology'],
  },
  {
    key: 'CC6B.3',
    parent: 'CC6B',
    name: 'Consciousness',
    subjects: ['psychology'],
  },
  {
    key: 'CC6B.4',
    parent: 'CC6B',
    name: 'Memory',
    subjects: ['psychology'],
  },
  {
    key: 'CC6B.5',
    parent: 'CC6B',
    name: 'Language',
    subjects: ['psychology'],
  },
  {
    key: 'CC6C',
    parent: 'FC6',
    name: 'Behavioral Responses',
    description: 'Focuses on mechanisms by which organisms respond to environmental stimuli.',
  },
  {
    key: 'CC6C.1',
    parent: 'CC6C',
    name: 'Emotion',
    subjects: ['psychology'],
  },
  {
    key: 'CC6C.2',
    parent: 'CC6C',
    name: 'Stress',
    subjects: ['psychology'],
  },
  {
    key: 'CC7A',
    parent: 'FC7',
    name: 'Individual Influences on Behavior',
    description: 'Focuses on psychological and biological factors that influence individual behavior.',
  },
  {
    key: 'CC7A.1',
    parent: 'CC7A',
    name: 'Biological Bases of Behavior',
    subjects: ['psychology', 'biology'],
  },
  {
    key: 'CC7A.2',
    parent: 'CC7A',
    name: 'Personality',
    subjects: ['psychology'],
  },
  {
    key: 'CC7A.3',
    parent: 'CC7A',
    name: 'Psychological Disorders',
    subjects: ['psychology'],
  },
  {
    key: 'CC7A.4',
    parent: 'CC7A',
    name: 'Motivation',
    subjects: ['psychology'],
  },
  {
    key: 'CC7A.5',
    parent: 'CC7A',
    name: 'Attitudes',
    subjects: ['psychology'],
  },
  {
    key: 'CC7B',
    parent: 'FC7',
    name: 'Social Influences on Behavior',
    description: 'Focuses on how social factors such as groups, norms, and social structures affect behavior.',
  },
  {
    key: 'CC7B.1',
    parent: 'CC7B',
    name: 'How the Presence of Others Affects Individual Behavior',
    subjects: ['psychology'],
  },
  {
    key: 'CC7B.2',
    parent: 'CC7B',
    name: 'Group Decision-Making Processes',
    subjects: ['psychology', 'sociology'],
  },
  {
    key: 'CC7B.3',
    parent: 'CC7B',
    name: 'Normative and Nonnormative Behavior',
    subjects: ['sociology'],
  },
  {
    key: 'CC7B.4',
    parent: 'CC7B',
    name: 'Socialization',
    subjects: ['psychology', 'sociology'],
  },
  {
    key: 'CC7C',
    parent: 'FC7',
    name: 'Learning and Attitude Change',
    description: 'Focuses on how learning and attitudes influence behavior and behavior change.',
  },
  {
    key: 'CC7C.1',
    parent: 'CC7C',
    name: 'Habituation and Dishabituation',
    subjects: ['psychology'],
  },
  {
    key: 'CC7C.2',
    parent: 'CC7C',
    name: 'Associative Learning',
    subjects: ['psychology'],
  },
  {
    key: 'CC7C.3',
    parent: 'CC7C',
    name: 'Observational Learning',
    subjects: ['psychology'],
  },
  {
    key: 'CC7C.4',
    parent: 'CC7C',
    name: 'Theories of Attitude and Behavior Change',
    subjects: ['psychology'],
  },
  {
    key: 'CC8A',
    parent: 'FC8',
    name: 'Self and Identity',
    description: 'Focuses on the concept of self and the formation of identity.',
  },
  {
    key: 'CC8A.1',
    parent: 'CC8A',
    name: 'Self-Concept, Self-Identity, and Social Identity',
    subjects: ['psychology', 'sociology'],
  },
  {
    key: 'CC8A.2',
    parent: 'CC8A',
    name: 'Formation of Identity',
    subjects: ['psychology', 'sociology'],
  },
  {
    key: 'CC8B',
    parent: 'FC8',
    name: 'Social Attitudes and Beliefs',
    description: 'Focuses on attitudes and beliefs that shape social interactions.',
  },
  {
    key: 'CC8B.1',
    parent: 'CC8B',
    name: 'Attributing Behavior to Persons or Situations',
    subjects: ['psychology'],
  },
  {
    key: 'CC8B.2',
    parent: 'CC8B',
    name: 'Prejudice and Bias',
    subjects: ['psychology', 'sociology'],
  },
  {
    key: 'CC8B.3',
    parent: 'CC8B',
    name: 'Processes Related to Stereotypes',
    subjects: ['psychology'],
  },
  {
    key: 'CC8C',
    parent: 'FC8',
    name: 'Social Interaction Processes',
    description: 'Focuses on actions and processes underlying social interactions.',
  },
  {
    key: 'CC8C.1',
    parent: 'CC8C',
    name: 'Elements of Social Interaction',
    subjects: ['psychology', 'sociology'],
  },
  {
    key: 'CC8C.2',
    parent: 'CC8C',
    name: 'Self-Presentation and Interacting With Others',
    subjects: ['psychology', 'sociology'],
  },
  {
    key: 'CC8C.3',
    parent: 'CC8C',
    name: 'Social Behavior',
    subjects: ['psychology'],
  },
  {
    key: 'CC8C.4',
    parent: 'CC8C',
    name: 'Discrimination',
    subjects: ['psychology', 'sociology'],
  },
  {
    key: 'CC9A',
    parent: 'FC9',
    name: 'Social Structure',
    description: 'Focuses on the relationship between social structures and human interactions.',
  },
  {
    key: 'CC9A.1',
    parent: 'CC9A',
    name: 'Theoretical Approaches',
    subjects: ['sociology'],
  },
  {
    key: 'CC9A.2',
    parent: 'CC9A',
    name: 'Social Institutions',
    subjects: ['sociology'],
  },
  {
    key: 'CC9A.3',
    parent: 'CC9A',
    name: 'Culture',
    subjects: ['psychology', 'sociology'],
  },
  {
    key: 'CC9B',
    parent: 'FC9',
    name: 'Demographics',
    description: 'Focuses on demographic characteristics and population processes within societies.',
  },
  {
    key: 'CC9B.1',
    parent: 'CC9B',
    name: 'Mortality and Fertility rate',
    subjects: ['sociology'],
  },
  {
    key: 'CC9B.2',
    parent: 'CC9B',
    name: 'Demographic Perspectives',
    subjects: ['sociology'],
  },
  {
    key: 'CC10A',
    parent: 'FC10',
    name: 'Social Stratification',
    description: 'Focuses on social class, stratification theories, social mobility, and poverty.',
  },
  {
    key: 'CC10A.1',
    parent: 'CC10A',
    name: 'Spatial Inequality',
    subjects: ['sociology'],
  },
  {
    key: 'CC10A.2',
    parent: 'CC10A',
    name: 'Social Class',
    subjects: ['sociology'],
  },
  {
    key: 'CC10A.3',
    parent: 'CC10A',
    name: 'Health Disparities',
    subjects: ['sociology'],
  },
  {
    key: 'CC10A.4',
    parent: 'CC10A',
    name: 'Health Care Disparities',
    subjects: ['sociology'],
  },
];

export const TOPICS_MAP = Object.fromEntries(TOPICS.map(t => [t.key, t]));

let _cachedTagMap = null;
export function getMCATTagsMap() {
  if (_cachedTagMap) return _cachedTagMap;
  _cachedTagMap = Object.fromEntries(
    [...Object.values(MCAT_SECTIONS), ...MCAT_SUBJECTS, ...CARS_TOPICS, ...MCAT_SKILLS, ...TOPICS].map(t => [t.key, t]),
  );
  return _cachedTagMap;
}
